import React, { useEffect, useState } from 'react';
import { ExternalLink, Link2, Plus, Save, Trash2, Edit3, Loader2 } from 'lucide-react';
import { COLORS, STORE_CATEGORIES } from '../../data/mockData';
import {
  fetchAdminAffiliates, addAdminAffiliate, updateAdminAffiliate, deleteAdminAffiliate,
} from '../../services/api';
import AdminModal, { adminInputStyle as inputStyle, adminLabelStyle as labelStyle } from './AdminModal';

const EMPTY_FORM = {
  name: '', description: '', price: '', category: 'health-devices',
  affiliate_url: '', partner: 'amazon', image_url: '', featured: false, active: true,
};

/**
 * Admin panel — Affiliates tab.
 * Manage partner & Amazon affiliate links; active items populate the Store.
 */
export default function AffiliatesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);   // product_id when editing
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const d = await fetchAdminAffiliates();
      setItems(d.affiliates || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load affiliates');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(EMPTY_FORM); setEditing(null); setErr(''); setShowForm(true); };
  const openEdit = (item) => {
    setForm({
      name: item.name, description: item.description || '', price: item.price ?? '',
      category: item.category || 'health-devices', affiliate_url: item.affiliate_url,
      partner: item.partner || 'amazon', image_url: item.image_url || '',
      featured: !!item.featured, active: item.active !== false,
    });
    setEditing(item.product_id);
    setErr('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.affiliate_url.trim()) {
      setErr('Name and affiliate URL are required');
      return;
    }
    setSaving(true);
    setErr('');
    const payload = {
      ...form,
      price: form.price === '' ? null : parseFloat(form.price),
      icon: 'Package',
    };
    try {
      if (editing) await updateAdminAffiliate(editing, payload);
      else await addAdminAffiliate(payload);
      setShowForm(false);
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to save affiliate product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete affiliate product "${item.name}"?`)) return;
    try { await deleteAdminAffiliate(item.product_id); load(); }
    catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="admin-affiliates">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif', margin: 0 }}>
            Affiliates &amp; Amazon Links ({items.length})
          </h3>
          <p style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 4 }}>
            Active affiliate products appear in the member Store. Members complete purchases on
            the partner's site via your affiliate link.
          </p>
        </div>
        <button data-testid="add-affiliate-btn" onClick={openAdd}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', borderRadius: 10, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
          <Plus size={16} /> Add Affiliate Link
        </button>
      </div>

      {err && !showForm && <p style={{ color: COLORS.error, fontSize: 13 }}>{err}</p>}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: COLORS.textSecondary }}><Loader2 size={20} className="spin" /></div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, backgroundColor: COLORS.surface, borderRadius: 14, marginTop: 12 }}>
          <Link2 size={40} color={COLORS.border} />
          <p style={{ color: COLORS.textSecondary, marginTop: 12 }}>
            No affiliate links yet. Add an Amazon Associates link (include your tracking tag,
            e.g. <code>?tag=yourtag-20</code>) to populate the Store.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
          {items.map(item => (
            <div key={item.product_id} data-testid={`affiliate-${item.product_id}`}
              style={{ backgroundColor: COLORS.surface, borderRadius: 12, padding: 16, display: 'flex', alignItems: 'center', gap: 14, opacity: item.active === false ? 0.55 : 1 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: '#FF990018', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <ExternalLink size={18} color="#B36B00" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.textPrimary }}>{item.name}</span>
                  <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 800, backgroundColor: '#FF990018', color: '#B36B00', textTransform: 'uppercase' }}>
                    {item.partner === 'amazon' ? 'Amazon' : 'Partner'}
                  </span>
                  {item.featured && <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 800, backgroundColor: `${COLORS.primary}18`, color: COLORS.primary }}>FEATURED</span>}
                  {item.active === false && <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 800, backgroundColor: '#99999918', color: '#777' }}>INACTIVE</span>}
                </div>
                <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 2, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <span>{item.category}</span>
                  {item.price != null && <span>${Number(item.price).toFixed(2)}</span>}
                  <a href={item.affiliate_url} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320, display: 'inline-block' }}>
                    {item.affiliate_url}
                  </a>
                </div>
              </div>
              <button data-testid={`edit-affiliate-${item.product_id}`} onClick={() => openEdit(item)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6 }}>
                <Edit3 size={16} color={COLORS.secondary} />
              </button>
              <button data-testid={`delete-affiliate-${item.product_id}`} onClick={() => handleDelete(item)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6 }}>
                <Trash2 size={16} color={COLORS.error} />
              </button>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <AdminModal onClose={() => setShowForm(false)} title={editing ? 'Edit Affiliate Link' : 'Add Affiliate Link'} testId="affiliate-modal" wide>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div><label style={labelStyle}>Product Name</label>
              <input data-testid="affiliate-name-input" placeholder="e.g. Oura Ring Gen 4" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} /></div>
            <div><label style={labelStyle}>Description</label>
              <textarea placeholder="Short description shown in the Store" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
            <div><label style={labelStyle}>Affiliate URL (include your tracking tag)</label>
              <input data-testid="affiliate-url-input" placeholder="https://www.amazon.com/dp/XXXX?tag=yourtag-20" value={form.affiliate_url} onChange={e => setForm({ ...form, affiliate_url: e.target.value })} style={inputStyle} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div><label style={labelStyle}>Partner</label>
                <select value={form.partner} onChange={e => setForm({ ...form, partner: e.target.value })} style={inputStyle}>
                  <option value="amazon">Amazon</option>
                  <option value="other">Other</option>
                </select></div>
              <div><label style={labelStyle}>Category</label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} style={inputStyle}>
                  {STORE_CATEGORIES.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select></div>
              <div><label style={labelStyle}>Display Price (optional)</label>
                <input type="number" step="0.01" min="0" placeholder="e.g. 299.00" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} style={inputStyle} /></div>
            </div>
            <div><label style={labelStyle}>Image URL (optional)</label>
              <input placeholder="https://..." value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} style={inputStyle} /></div>
            <div style={{ display: 'flex', gap: 20 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: COLORS.textPrimary, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.featured} onChange={e => setForm({ ...form, featured: e.target.checked })} /> Featured
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: COLORS.textPrimary, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} /> Active (visible in Store)
              </label>
            </div>
            {err && <p style={{ color: COLORS.error, fontSize: 13, margin: 0 }}>{err}</p>}
            <button data-testid="save-affiliate-btn" onClick={handleSave} disabled={saving}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px', borderRadius: 10, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, fontSize: 15, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? <Loader2 size={16} className="spin" /> : <Save size={16} />} {editing ? 'Update Affiliate' : 'Add Affiliate'}
            </button>
          </div>
        </AdminModal>
      )}
    </div>
  );
}
